import { authReturnPath } from "@lib/authReturnPath";
import { FormInput } from "@client/components/forms/form-input";
import { saveAuthToken } from "@client/iphone/lib/auth";
import { OAuthSession } from "@client/iphone/lib/OAuthSessionPlugin";
import { parseMobileAuthCallback } from "@client/iphone/lib/mobileOAuth";
import { generatePkcePair } from "@client/iphone/lib/pkce";
import { getClientAbsoluteBaseUrl, trpcVanilla } from "@client/trpc";
import {
  MOBILE_AUTH_ACCESS_DENIED,
  MOBILE_AUTH_INVALID_STATE,
  MOBILE_AUTH_SIGNIN_INCOMPLETE,
  MOBILE_OAUTH_CALLBACK_SCHEME,
} from "@lib/auth/shared/mobileOauthConstants";
import { Capacitor } from "@capacitor/core";
import { zodResolver } from "@hookform/resolvers/zod";
import { TRPCClientError } from "@trpc/client";
import {
  Button,
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  Form,
} from "@sfc/ui";
import { CheckCircleIcon, MailIcon } from "lucide-react";
import { type ReactNode, useState } from "react";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import * as z from "zod";
import GoogleLogo from "./GoogleLogo";

export default function LoginButton({
  ...rest
}: React.ComponentProps<typeof Button>) {
  const [isModalOpen, setIsModalOpen] = useState(false);

  return (
    <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
      <DialogTrigger render={<Button {...rest} />}>Get Started</DialogTrigger>
      <DialogContent className="sm:max-w-md gap-6" showCloseButton={false}>
        <LoginModal />
      </DialogContent>
    </Dialog>
  );
}

const emailSchema = z.object({
  email: z.string().email("Please enter a valid email address"),
});

type EmailFormValues = z.infer<typeof emailSchema>;

export function GoogleSignInButton() {
  const [loading, setLoading] = useState(false);

  const handleGoogleSignIn = async () => {
    const returnTo = authReturnPath(
      window.location.pathname + window.location.search,
    );
    setLoading(true);

    try {
      // Native shell (iPad + iPhone): run Google sign-in through an
      // ASWebAuthenticationSession (system browser sheet — Google permits it,
      // unlike an embedded WKWebView). We generate a PKCE pair and pass the
      // challenge to the server; /api/mobile-oauth-finish redirects to our
      // custom scheme with a short-lived authorization *code* (not the session
      // token). We then exchange that code — with the verifier — for the real
      // session token over HTTPS, store it, and reload. See
      // mobileOauthFinishHandler + exchangeMobileAuthCode in lib/auth/server.
      // (Replaces an old iPhone branch that did a top-level navigation, which
      // escapes a bundled build to Safari and never returns.)
      if (Capacitor.isNativePlatform()) {
        const base = getClientAbsoluteBaseUrl();
        const callbackUrl = encodeURIComponent("/api/mobile-oauth-finish");
        // Safari can drop the flow's cookies mid-flight (bounce-tracking
        // protection, typically on a device's first-ever sign-in attempt).
        // The server hands those failures back as a retryable error code —
        // `signin_incomplete` from the /auth-error page when the Google
        // callback loses its PKCE cookie, `invalid_state` from
        // mobile-oauth-finish when the flow-state cookie is lost — and one
        // automatic restart reliably succeeds, because the failed attempt
        // established the first-party engagement Safari wants.
        const retryableErrors = new Set<string>([
          MOBILE_AUTH_SIGNIN_INCOMPLETE,
          MOBILE_AUTH_INVALID_STATE,
        ]);

        for (let attempt = 0; ; attempt++) {
          const { verifier, challenge } = await generatePkcePair();
          const { url } = await OAuthSession.start({
            url: `${base}/api/mobile-oauth-init/google?callbackUrl=${callbackUrl}&code_challenge=${encodeURIComponent(challenge)}`,
            callbackScheme: MOBILE_OAUTH_CALLBACK_SCHEME,
          });
          const { code, error: callbackError } = parseMobileAuthCallback(url);

          if (code) {
            const { sessionToken } =
              await trpcVanilla.exchangeMobileAuthCode.mutate({
                code,
                codeVerifiers: [verifier],
              });
            const bearerChanged = saveAuthToken(sessionToken);

            if (returnTo !== "/") {
              window.location.replace(returnTo);
            } else if (bearerChanged) {
              // Identity transition (a different account's stale token was
              // replaced): a reload would inherit the current URL, which the
              // iPad boot redirect may have rewritten to the previous user's
              // saved round. Land the new session on "/".
              window.location.replace("/");
            } else {
              window.location.reload();
            }

            return;
          }

          if (
            attempt === 0 &&
            callbackError &&
            retryableErrors.has(callbackError)
          ) {
            continue;
          }

          throw new Error(callbackError ?? "Sign-in did not complete");
        }
      }

      // Fetch CSRF token from Auth.js
      const csrfResponse = await fetch("/auth/csrf");
      const { csrfToken } = await csrfResponse.json();

      // Create and submit a form to POST to the signin endpoint
      const form = document.createElement("form");
      form.method = "POST";
      form.action = "/auth/signin/google";

      const csrfInput = document.createElement("input");
      csrfInput.type = "hidden";
      csrfInput.name = "csrfToken";
      csrfInput.value = csrfToken;
      form.appendChild(csrfInput);

      const callbackInput = document.createElement("input");
      callbackInput.type = "hidden";
      callbackInput.name = "callbackUrl";
      callbackInput.value = authReturnPath(
        window.location.pathname + window.location.search,
      );
      form.appendChild(callbackInput);

      document.body.appendChild(form);
      form.submit();
    } catch (error) {
      console.error("Error initiating Google sign-in:", error);

      // "cancelled" just means the user dismissed the native auth sheet.
      if (
        error instanceof Error &&
        error.message === MOBILE_AUTH_ACCESS_DENIED
      ) {
        toast.error("This account doesn't have permission to sign in.");
      } else if (!(error instanceof Error && error.message === "cancelled")) {
        toast.error("Couldn't sign in with Google. Please try again.");
      }

      setLoading(false);
    }
  };

  return (
    <Button
      variant="outline"
      className="w-full bg-transparent"
      size="lg"
      onClick={handleGoogleSignIn}
      isLoading={loading}
    >
      <GoogleLogo className="size-4" />
      Continue with Google
    </Button>
  );
}

/**
 * The shared sign-in UI: "Continue with Google" + a magic-link email form, with
 * a post-send "check your email" state. Used both inside the web `LoginModal`
 * (in a Dialog) and standalone in the iPhone shell's login screen, so it takes
 * no Dialog dependency. `closeSlot` lets the dialog inject its own close button
 * into the post-send state; the iPhone screen omits it.
 */
export function LoginPanel({
  closeSlot,
  variant = "default",
}: {
  closeSlot?: ReactNode;
  variant?: "default" | "welcome";
}) {
  const [loading, setLoading] = useState(false);
  const [emailSent, setEmailSent] = useState(false);

  const form = useForm<EmailFormValues>({
    resolver: zodResolver(emailSchema),
    defaultValues: {
      email: "",
    },
  });

  const onSubmitEmail = async (values: EmailFormValues) => {
    setLoading(true);

    try {
      await trpcVanilla.sendMagicLink.mutate({
        email: values.email,
        returnTo: authReturnPath(
          window.location.pathname + window.location.search,
        ),
        // On a native shell (iPad + iPhone), ask the server to build a magic
        // link that deep-links back into the app instead of landing in Safari.
        // The code it hands back is short-lived + single-use (no PKCE — the
        // emailed link is itself the secret).
        mobile: Capacitor.isNativePlatform(),
        // Where we're signing in from. The server uses this ONLY in dev (where
        // the link is printed to the console, not emailed) so on-device testing
        // gets a reachable host — e.g. http://<mac>.local:3004 — instead of a
        // static localhost. Ignored in production (see sendMagicLinkEmail).
        origin: window.location.origin,
      });
      setEmailSent(true);
    } catch (error) {
      if (
        error instanceof TRPCClientError &&
        error.data?.code === "TOO_MANY_REQUESTS"
      ) {
        toast.error(error.message);
      } else {
        console.error("Error sending magic link:", error);
        toast.error("Failed to send magic link");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex flex-col gap-4 text-center">
      {emailSent ? (
        <>
          <div className="self-center rounded-full bg-green-100 p-3">
            <CheckCircleIcon className="h-8 w-8 text-green-600" />
          </div>
          <div className="flex flex-col gap-2">
            <h3 className="font-semibold">Check your email</h3>
            <p className="text-sm text-muted-foreground">
              We've sent a magic link to{" "}
              <strong>{form.getValues("email")}</strong>
            </p>
            <p className="text-xs text-muted-foreground">
              Click the link in your email to sign in. The link will expire in
              24 hours.
            </p>
          </div>
          {closeSlot}
        </>
      ) : (
        <>
          <GoogleSignInButton />

          <div className="flex items-center gap-2">
            <hr className="flex-1" />
            <span className="text-xs text-muted-foreground">Or</span>
            <hr className="flex-1" />
          </div>

          <Form {...form}>
            <form
              onSubmit={form.handleSubmit(onSubmitEmail)}
              className="flex flex-col gap-3"
            >
              {variant === "default" && (
                <p className="text-sm text-muted-foreground">
                  Get a magic link sent to your email
                </p>
              )}

              <FormInput
                control={form.control}
                name="email"
                label={variant === "welcome" ? "Email address" : undefined}
                labelClassName="text-left"
                autoComplete="email"
                placeholder="Enter your email address"
                type="email"
              />

              <Button type="submit" isLoading={loading} size="lg">
                <MailIcon />
                {variant === "welcome"
                  ? "Email me a sign-in link"
                  : "Send Magic Link"}
              </Button>

              <p className="text-xs text-muted-foreground">
                {variant === "welcome"
                  ? "No password needed. We’ll email you a secure sign-in link."
                  : "No password required. We'll send you a secure link to sign in."}
              </p>
            </form>
          </Form>
        </>
      )}
      <a
        href={`/privacy?${new URLSearchParams({ returnTo: authReturnPath(window.location.pathname + window.location.search) })}`}
        className="inline-flex min-h-11 items-center justify-center self-center rounded-sm body-sm text-muted-foreground underline underline-offset-4 hover:text-foreground focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-ring"
      >
        Privacy policy
      </a>
    </div>
  );
}

export function LoginModal() {
  return (
    <>
      <DialogHeader className="sm:text-center">
        <DialogTitle>Welcome to S-Process</DialogTitle>
        <DialogDescription className="sr-only">
          Sign in to your S-Process account.
        </DialogDescription>
      </DialogHeader>

      <LoginPanel
        closeSlot={
          <DialogClose render={<Button variant="outline" />}>Close</DialogClose>
        }
      />
    </>
  );
}
